import React, { FC, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router-dom';
import * as analytics from '@adsk/acs-web-analytics';
import ErrorPage from '@adsk/alloy-error-page';
import { report, REPORT_TYPES } from '@adsk/exo-skeleton-api';
import { ANALYTICS_TYPES } from './analytics';
import ExoSkeletonApi from './components/ExoSkeletonApi/ExoSkeletonApi';
import { LaunchDarklyProvider } from './components/LaunchDarklyProvider';
import Router from './components/Router/Router';
import RemoteOverridesProvider from './providers/RemoteOverridesProvider';
import i18n, { initi18n } from './utils/i18n';
import isLocalError from './utils/isLocalError';
import { disposeMonitoringContext } from './utils/monitoring';

const REPORT_ERROR_TYPE = 'root-error-boundary';

const App: FC = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      await initi18n();
      setLoading(false);
    };

    void init();
  }, []);

  useEffect(() => {
    analytics.track(ANALYTICS_TYPES.EXO_LOAD, {
      component_name: 'ExoSkeleton',
    });

    return () => {
      disposeMonitoringContext();
    };
  }, []);

  return (
    <BrowserRouter>
      <LaunchDarklyProvider>
        <RemoteOverridesProvider>
          <ExoSkeletonApi />
          <div>
            <ErrorBoundary
              fallbackRender={() => (
                <ErrorPage
                  title={i18n.t('ERROR_PAGE_TITLE')}
                  description={i18n.t('ERROR_PAGE_DESCRIPTION')}
                />
              )}
              onError={(err) => {
                if (isLocalError(err)) return; // Don't report local errors

                analytics.track(ANALYTICS_TYPES.EXO_LOAD_ERROR, {
                  error_description: err.message,
                });

                void report({
                  type: REPORT_TYPES.error,
                  value: err,
                  context: {
                    type: REPORT_ERROR_TYPE,
                  },
                });
              }}
            >
              {loading ? null : <Router />}
            </ErrorBoundary>
          </div>
        </RemoteOverridesProvider>
      </LaunchDarklyProvider>
    </BrowserRouter>
  );
};

export default App;
